import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { locationCardBackdrop } from '../game/locationBackgrounds'
import { getPortraitSrc } from '../game/portraits'
import { useI18n } from '../i18n/useI18n'
import { LocationLootPreview } from './LocationLootPreview'
import {
  estimateLootYieldVsBaselinePct,
  NEEDS_LOOT_TUNING,
} from '../game/loot'
import {
  getLocationExplorationPct,
  isLocationUnlocked,
  LOCATION_LIST,
  LOCATIONS,
} from '../game/locations'
import {
  formatTripDurationCompact,
  scaledExpeditionDurationMs,
} from '../game/expeditionPartyDuration'
import { starterQuestRestrictsExpeditions, starterQuestUsesTutorialDuration } from '../game/quests'
import { vehicleIsOnActiveExpedition } from '../game/garageOps'
import {
  getVehicleDef,
  getVehicleEffectiveStatsForInstance,
  vehicleLootYieldParams,
} from '../game/vehicles'
import { WORKSTATIONS } from '../game/workstations'
import type {
  LocationExplorationState,
  LocationId,
  Survivor,
  VehicleInstance,
  WorkstationId,
} from '../game/types'
import { useGameStore } from '../store/useGameStore'

/** Survivor need stat display — bands match NEEDS_LOOT_TUNING */
function needValueClass(value: number): string {
  const v = Math.round(value)
  const { criticalBelow, lowBandMax } = NEEDS_LOOT_TUNING
  const base = 'font-mono tabular-nums'
  if (v < criticalBelow) return `${base} text-red-400`
  if (v <= lowBandMax) return `${base} text-orange-400`
  return `${base} text-stone-400`
}

function recruitRetryOfferAvailable(
  locationId: LocationId,
  locationExploration: Partial<Record<LocationId, LocationExplorationState>>,
  survivors: Survivor[],
): boolean {
  const recruit = LOCATIONS[locationId].exploration.recruit
  if (!recruit) return false
  if (survivors.some((s) => s.id === recruit.survivor.id)) return false
  return locationExploration[locationId]?.recruitOffered === true
}

type Props = {
  survivor: Survivor | null
  presetLocationId?: LocationId | null
  onClose: () => void
}

export function AssignSheet({ survivor, presetLocationId, onClose }: Props) {
  const { t, pick } = useI18n()
  const survivors = useGameStore((s) => s.survivors)
  const startExpedition = useGameStore((s) => s.startExpedition)
  const allIdleSurvivors = useGameStore(
    useShallow((s) => s.survivors.filter((x) => x.status === 'Idle')),
  )
  const injuredSurvivors = useGameStore(
    useShallow((s) => s.survivors.filter((x) => x.status === 'Injured')),
  )
  const workstationAssignments = useGameStore(
    useShallow((s) => {
      const map: Record<string, WorkstationId> = {}
      for (const [wsId, ws] of Object.entries(s.workstations)) {
        if (ws?.assignedSurvivorId) {
          map[ws.assignedSurvivorId] = wsId as WorkstationId
        }
      }
      return map
    }),
  )
  const lodgingOccupantIds = useGameStore(
    useShallow((s) => s.workstations.lodging?.lodgingOccupantIds ?? []),
  )
  const idleSurvivors = allIdleSurvivors.filter(
    (s) => !workstationAssignments[s.id] && !lodgingOccupantIds.includes(s.id),
  )
  const workstationSurvivors = allIdleSurvivors
    .filter((s) => workstationAssignments[s.id])
    .map((s) => ({
      survivor: s,
      workstationName: WORKSTATIONS[workstationAssignments[s.id]].name,
    }))
  const restingSurvivors = allIdleSurvivors.filter((s) =>
    lodgingOccupantIds.includes(s.id),
  )
  const progress = useGameStore((s) => s.progress)
  const locationExploration = useGameStore((s) => s.locationExploration)
  const quests = useGameStore((s) => s.quests)
  const garageVehicles = useGameStore((s) => s.garageVehicles)
  const expeditions = useGameStore((s) => s.expeditions)
  const workstations = useGameStore((s) => s.workstations)
  const restrictStarterBridge = useGameStore((s) =>
    starterQuestRestrictsExpeditions(s.quests),
  )
  const isOpen = !!(survivor || presetLocationId)
  const [presetSelectedSurvivorIds, setPresetSelectedSurvivorIds] = useState<string[]>([])
  const [selectedVehicleInstanceIds, setSelectedVehicleInstanceIds] = useState<string[]>([])
  const [retryRecruitToggle, setRetryRecruitToggle] = useState(false)

  const activePresetSelection = presetSelectedSurvivorIds.filter((id) =>
    idleSurvivors.some((s) => s.id === id),
  )
  const assignableSurvivorIds = new Set(idleSurvivors.map((s) => s.id))
  const survivorCanStart = survivor ? assignableSurvivorIds.has(survivor.id) : false

  let unlockedLocations = LOCATION_LIST.filter((location) =>
    isLocationUnlocked(location.id, progress, locationExploration, quests),
  )
  if (restrictStarterBridge) {
    unlockedLocations = unlockedLocations.filter(
      (location) => location.id === 'vinkel-bridge',
    )
  }
  const sheetBackdropLocationId = presetLocationId ?? unlockedLocations[0]?.id ?? 'vinkel-bridge'
  const sheetBackdrop = locationCardBackdrop(sheetBackdropLocationId)

  // Only show vehicles that are in the garage (garage built), have durability > 0,
  // and are not currently out on an active expedition
  const garageBuilt = workstations.garage?.built === true
  const availableVehicles: VehicleInstance[] = garageBuilt
    ? garageVehicles.filter(
        (v) =>
          v.durability > 0 && !vehicleIsOnActiveExpedition(expeditions, v.id),
      )
    : []

  useEffect(() => {
    if (isOpen) setRetryRecruitToggle(false)
  }, [isOpen, presetLocationId, survivor?.id])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  const handleStart = (survivorIds: string[], locationId: LocationId) => {
    if (survivorIds.some((id) => !assignableSurvivorIds.has(id))) return
    const retryRecruit =
      retryRecruitToggle &&
      recruitRetryOfferAvailable(locationId, locationExploration, survivors)
    startExpedition(
      survivorIds,
      locationId,
      selectedVehicleInstanceIds.length > 0 ? selectedVehicleInstanceIds : undefined,
      retryRecruit ? { retryRecruit: true } : undefined,
    )
    onClose()
  }

  const presetRetryEligible =
    presetLocationId != null &&
    recruitRetryOfferAvailable(presetLocationId, locationExploration, survivors)

  const presetRetryRecruitName =
    presetRetryEligible && presetLocationId
      ? LOCATIONS[presetLocationId].exploration.recruit?.survivor.name ?? ''
      : ''

  const survivorFlowRetryEligible =
    survivor != null &&
    unlockedLocations.some((loc) =>
      recruitRetryOfferAvailable(loc.id, locationExploration, survivors),
    )

  const survivorFlowRetryRecruitName =
    unlockedLocations.find((loc) =>
      recruitRetryOfferAvailable(loc.id, locationExploration, survivors),
    )?.exploration.recruit?.survivor.name ?? ''

  const selectedVehicleEffStats = selectedVehicleInstanceIds
    .map((id) => garageVehicles.find((v) => v.id === id))
    .filter((v): v is NonNullable<typeof v> => v != null)
    .map((v) => getVehicleEffectiveStatsForInstance(v))

  const totalSelectedSeats = selectedVehicleEffStats.reduce((sum, d) => sum + d.passengerSeats, 0)
  const vehicleLootBonusPct =
    selectedVehicleEffStats.length > 0
      ? Math.round(
          (selectedVehicleEffStats.reduce((sum, d) => sum + d.lootMultiplier, 0) /
            selectedVehicleEffStats.length -
            1) *
            100,
        )
      : 0

  const vehicleDurationMultiplier =
    selectedVehicleEffStats.length > 0
      ? selectedVehicleEffStats.reduce((sum, s) => sum + s.durationMultiplier, 0) /
        selectedVehicleEffStats.length
      : 1

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="assign-sheet"
          className="fixed inset-0 z-50 flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/70" />
          <motion.div
            className="relative w-full max-w-[430px] overflow-hidden rounded-t-2xl border-t border-stone-700 bg-stone-950 p-4 pb-6 shadow-2xl"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={sheetBackdrop.src}
              alt=""
              aria-hidden
              decoding="async"
              className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover opacity-50 select-none"
              style={{ filter: `${sheetBackdrop.brightnessFilter} saturate(0.75)` }}
            />
            <div className="pointer-events-none absolute inset-0 z-1 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.1),rgba(0,0,0,0.3))]" />
            <div className="relative z-2">
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-stone-700" />

              {survivor ? (
                <>
                  <h2 className="text-base font-semibold text-stone-100">
                    {t('assign_send_title', { name: survivor.name })}
                  </h2>
                  <p className="mb-3 text-xs text-stone-400">
                    {t('assign_pick_destination')}
                  </p>
                  {availableVehicles.length > 0 && (
                    <VehiclePicker
                      vehicles={availableVehicles}
                      selectedVehicleInstanceIds={selectedVehicleInstanceIds}
                      onToggle={(id) =>
                        setSelectedVehicleInstanceIds((prev) =>
                          prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                        )
                      }
                    />
                  )}
                  <ul className="max-h-[60vh] space-y-2 overflow-y-auto">
                    {unlockedLocations.map((loc) => (
                      <li key={loc.id}>
                        <button
                          type="button"
                          disabled={!survivorCanStart}
                          onClick={() => handleStart([survivor.id], loc.id)}
                          className="w-full rounded-md border border-stone-800 bg-stone-800 p-3 text-left transition-colors hover:border-amber-800 hover:bg-stone-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-stone-100">
                              {pick(loc.name)}
                            </span>
                            <span className="font-mono text-xs tabular-nums text-stone-400">
                              {formatTripDurationCompact(loc.durationMs)}
                            </span>
                          </div>
                          <p className="mt-0.5 text-xs italic text-stone-400">
                            {pick(loc.blurb)}
                          </p>
                          <LocationLootPreview
                            loot={loc.loot}
                            className="mt-2"
                            obfuscated={
                              getLocationExplorationPct(locationExploration, loc.id) <
                              loc.exploration.lootRevealAtPct
                            }
                            recruitPreview={
                              loc.exploration.recruit
                                ? {
                                    portraitKey: loc.exploration.recruit.survivor.portrait,
                                    recruited: survivors.some(
                                      (s) => s.id === loc.exploration.recruit!.survivor.id,
                                    ),
                                  }
                                : undefined
                            }
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                  {survivorFlowRetryEligible ? (
                    <RecruitRetryOfferToggle
                      recruitName={survivorFlowRetryRecruitName}
                      checked={retryRecruitToggle}
                      onChange={setRetryRecruitToggle}
                    />
                  ) : null}
                </>
              ) : presetLocationId ? (
                <>
                  <PresetLocationView
                    locationId={presetLocationId}
                    idleSurvivors={idleSurvivors}
                    injuredSurvivors={injuredSurvivors}
                    workstationSurvivors={workstationSurvivors}
                    restingSurvivors={restingSurvivors}
                    selectedSurvivorIds={activePresetSelection}
                    onToggleSurvivor={(id) =>
                      setPresetSelectedSurvivorIds((prev) =>
                        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                      )
                    }
                    vehicleLootBonusPct={vehicleLootBonusPct}
                    vehicleDurationMultiplier={vehicleDurationMultiplier}
                  />
                  {availableVehicles.length > 0 && (
                    <VehiclePicker
                      vehicles={availableVehicles}
                      selectedVehicleInstanceIds={selectedVehicleInstanceIds}
                      onToggle={(id) =>
                        setSelectedVehicleInstanceIds((prev) =>
                          prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                        )
                      }
                    />
                  )}
                  {presetRetryEligible ? (
                    <RecruitRetryOfferToggle
                      recruitName={presetRetryRecruitName}
                      checked={retryRecruitToggle}
                      onChange={setRetryRecruitToggle}
                    />
                  ) : null}
                </>
              ) : null}

              {presetLocationId && activePresetSelection.length > 0 ? (
                <>
                  {selectedVehicleEffStats.length > 0 &&
                    activePresetSelection.length > totalSelectedSeats && (
                      <p className="mt-3 text-center text-[10px] text-stone-500">
                        {t('assign_vehicle_seat_hint')}
                      </p>
                    )}
                  <button
                    type="button"
                    onClick={() => handleStart(activePresetSelection, presetLocationId)}
                    disabled={
                      selectedVehicleEffStats.length > 0 &&
                      activePresetSelection.length > totalSelectedSeats
                    }
                    className="mt-4 w-full rounded-md border border-amber-700 bg-amber-900/40 px-3 py-2 text-sm font-semibold uppercase tracking-wider text-amber-100 hover:bg-amber-900/70 disabled:cursor-not-allowed disabled:border-stone-800 disabled:bg-stone-800 disabled:text-stone-600"
                  >
                    {selectedVehicleEffStats.length > 0 &&
                    activePresetSelection.length > totalSelectedSeats
                      ? t('assign_not_enough_seats')
                      : t('assign_send')}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-4 w-full rounded-md border border-stone-800 bg-stone-800 px-3 py-2 text-sm uppercase tracking-wider text-stone-400 hover:bg-stone-800"
                >
                  {t('assign_cancel')}
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function RecruitRetryOfferToggle({
  recruitName,
  checked,
  onChange,
}: {
  recruitName: string
  checked: boolean
  onChange: (next: boolean) => void
}) {
  const { t } = useI18n()
  return (
    <div className="mt-3 rounded-md border border-amber-900/45 bg-stone-900/55 p-3">
      <p className="mb-2 text-[11px] leading-snug text-stone-400">
        {t('assign_recruit_retry_hint')}
      </p>
      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-stone-600 bg-stone-900 accent-amber-600"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="text-xs leading-snug text-stone-200">
          {t('assign_recruit_retry_toggle', { name: recruitName })}
        </span>
      </label>
    </div>
  )
}

function PresetLocationView({
  locationId,
  idleSurvivors,
  injuredSurvivors,
  workstationSurvivors,
  restingSurvivors,
  selectedSurvivorIds,
  onToggleSurvivor,
  vehicleLootBonusPct,
  vehicleDurationMultiplier,
}: {
  locationId: LocationId
  idleSurvivors: Survivor[]
  injuredSurvivors: Survivor[]
  workstationSurvivors: Array<{ survivor: Survivor; workstationName: { nl: string; en: string } }>
  restingSurvivors: Survivor[]
  selectedSurvivorIds: string[]
  onToggleSurvivor: (id: string) => void
  vehicleLootBonusPct: number
  vehicleDurationMultiplier: number
}) {
  const { t, pick } = useI18n()
  const locationExploration = useGameStore((s) => s.locationExploration)
  const rosterSurvivors = useGameStore((s) => s.survivors)
  const quests = useGameStore((s) => s.quests)
  const loc = LOCATION_LIST.find((l) => l.id === locationId)
  if (!loc) return null
  const baseDurationMs = starterQuestUsesTutorialDuration(quests) ? 20_000 : loc.durationMs
  const partyCount = Math.max(1, selectedSurvivorIds.length)
  const tripDurationMs = scaledExpeditionDurationMs(baseDurationMs, partyCount, vehicleDurationMultiplier)
  const extraTripDurationMs = tripDurationMs - baseDurationMs
  return (
    <>
      <div className="mb-2 flex items-start justify-between gap-3">
        <h2 className="min-w-0 flex-1 text-base font-semibold leading-snug text-stone-100">
          {t('assign_who_goes', { place: pick(loc.name) })}
        </h2>
        <div className="shrink-0 text-right leading-none">
          <div className="font-mono text-2xl font-bold tabular-nums tracking-tight text-stone-100">
            {formatTripDurationCompact(tripDurationMs)}
          </div>
          {partyCount > 1 && extraTripDurationMs > 0 && (
            <div className="mt-1 text-[10px] font-semibold tabular-nums text-amber-400/95">
              +{formatTripDurationCompact(extraTripDurationMs)}
            </div>
          )}
        </div>
      </div>
      <p className="mb-2 text-xs italic text-stone-400">{pick(loc.blurb)}</p>
      <LocationLootPreview
        loot={loc.loot}
        className="mb-3"
        obfuscated={
          getLocationExplorationPct(locationExploration, loc.id) <
          loc.exploration.lootRevealAtPct
        }
        recruitPreview={
          loc.exploration.recruit
            ? {
                portraitKey: loc.exploration.recruit.survivor.portrait,
                recruited: rosterSurvivors.some(
                  (s) => s.id === loc.exploration.recruit!.survivor.id,
                ),
              }
            : undefined
        }
      />
      {idleSurvivors.length === 0 &&
      injuredSurvivors.length === 0 &&
      workstationSurvivors.length === 0 &&
      restingSurvivors.length === 0 ? (
        <p className="rounded-md border border-stone-800 bg-stone-800 p-3 text-sm text-stone-500">
          {t('assign_no_idle')}
        </p>
      ) : (
        <ul className="space-y-2">
          {idleSurvivors.map((s) => {
            const yieldPct = estimateLootYieldVsBaselinePct(s, loc.rolls)
            const totalPct = yieldPct + vehicleLootBonusPct
            const portraitSrc = getPortraitSrc(s.portrait)
            const survivorInitials = s.name.slice(0, 2).toUpperCase()
            const yieldClass =
              totalPct > 0
                ? 'text-emerald-400'
                : totalPct < 0
                  ? 'text-red-400'
                  : 'text-stone-500'
            return (
              <li key={s.id}>
                <button
                  type="button"
                  aria-pressed={selectedSurvivorIds.includes(s.id)}
                  onClick={() => onToggleSurvivor(s.id)}
                  className={`w-full rounded-md border p-3 text-left transition-colors ${
                    selectedSurvivorIds.includes(s.id)
                      ? 'border-amber-600 bg-amber-950/35 ring-1 ring-amber-700/50'
                      : 'border-stone-800 bg-stone-800 hover:border-amber-800 hover:bg-stone-800'
                  }`}
                >
                  <div className="space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex flex-1 items-start gap-2">
                        <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md border border-stone-700 bg-linear-to-br from-stone-800 to-stone-950">
                          {portraitSrc ? (
                            <img
                              src={portraitSrc}
                              alt={s.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="grid h-full w-full place-items-center font-mono text-[10px] font-bold text-stone-300">
                              {survivorInitials}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-semibold text-stone-100">{s.name}</div>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {vehicleLootBonusPct !== 0 && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400">
                            +{vehicleLootBonusPct}%
                          </span>
                        )}
                        <span
                          className={`text-[10px] font-semibold uppercase tracking-wider tabular-nums ${yieldClass}`}
                        >
                          {t('assign_loot_yield_pct', {
                            sign: totalPct >= 0 ? '+' : '-',
                            pct: Math.abs(totalPct),
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-baseline gap-x-1 text-[10px] uppercase tracking-wider">
                      <span className="text-stone-500">{t('need_hunger')}</span>{' '}
                      <span className={needValueClass(s.needs.hunger)}>
                        {Math.round(s.needs.hunger)}
                      </span>
                      <span className="text-stone-600">·</span>
                      <span className="text-stone-500">{t('need_thirst')}</span>{' '}
                      <span className={needValueClass(s.needs.thirst)}>
                        {Math.round(s.needs.thirst)}
                      </span>
                      <span className="text-stone-600">·</span>
                      <span className="text-stone-500">{t('need_energy')}</span>{' '}
                      <span className={needValueClass(s.needs.energy)}>
                        {Math.round(s.needs.energy)}
                      </span>
                    </div>
                  </div>
                </button>
              </li>
            )
          })}
          {workstationSurvivors.map(({ survivor: s, workstationName }) => {
            const portraitSrc = getPortraitSrc(s.portrait)
            const survivorInitials = s.name.slice(0, 2).toUpperCase()
            return (
              <li key={s.id}>
                <button
                  type="button"
                  disabled
                  className="w-full cursor-not-allowed rounded-md border border-stone-800 bg-stone-900/50 p-3 text-left opacity-70"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex flex-1 items-start gap-2">
                      <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md border border-stone-700 bg-linear-to-br from-stone-800 to-stone-950">
                        {portraitSrc ? (
                          <img
                            src={portraitSrc}
                            alt={s.name}
                            className="h-full w-full object-cover grayscale"
                          />
                        ) : (
                          <div className="grid h-full w-full place-items-center font-mono text-[10px] font-bold text-stone-500">
                            {survivorInitials}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-semibold text-stone-400">{s.name}</div>
                      </div>
                    </div>
                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-amber-400">
                      {t('assign_at_workstation', { name: pick(workstationName) })}
                    </span>
                  </div>
                </button>
              </li>
            )
          })}
          {restingSurvivors.map((s) => {
            const portraitSrc = getPortraitSrc(s.portrait)
            const survivorInitials = s.name.slice(0, 2).toUpperCase()
            return (
              <li key={s.id}>
                <button
                  type="button"
                  disabled
                  className="w-full cursor-not-allowed rounded-md border border-stone-800 bg-stone-900/50 p-3 text-left opacity-70"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex flex-1 items-start gap-2">
                      <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md border border-stone-700 bg-linear-to-br from-stone-800 to-stone-950">
                        {portraitSrc ? (
                          <img
                            src={portraitSrc}
                            alt={s.name}
                            className="h-full w-full object-cover grayscale"
                          />
                        ) : (
                          <div className="grid h-full w-full place-items-center font-mono text-[10px] font-bold text-stone-500">
                            {survivorInitials}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-semibold text-stone-400">{s.name}</div>
                      </div>
                    </div>
                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-emerald-400">
                      {t('status_resting')}
                    </span>
                  </div>
                </button>
              </li>
            )
          })}
          {injuredSurvivors.map((s) => {
            const portraitSrc = getPortraitSrc(s.portrait)
            const survivorInitials = s.name.slice(0, 2).toUpperCase()
            return (
              <li key={s.id}>
                <button
                  type="button"
                  disabled
                  className="w-full cursor-not-allowed rounded-md border border-stone-800 bg-stone-900/50 p-3 text-left opacity-70"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex flex-1 items-start gap-2">
                      <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md border border-stone-700 bg-linear-to-br from-stone-800 to-stone-950">
                        {portraitSrc ? (
                          <img
                            src={portraitSrc}
                            alt={s.name}
                            className="h-full w-full object-cover grayscale"
                          />
                        ) : (
                          <div className="grid h-full w-full place-items-center font-mono text-[10px] font-bold text-stone-500">
                            {survivorInitials}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-semibold text-stone-400">{s.name}</div>
                      </div>
                    </div>
                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-red-400">
                      INJURED
                    </span>
                  </div>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}

function VehiclePicker({
  vehicles,
  selectedVehicleInstanceIds,
  onToggle,
}: {
  vehicles: VehicleInstance[]
  selectedVehicleInstanceIds: string[]
  onToggle: (id: string) => void
}) {
  const { t, pick } = useI18n()
  return (
    <div className="mt-3">
      <div className="mb-1 text-[10px] uppercase tracking-wider text-stone-500">
        {t('assign_vehicle_label')}
      </div>
      <div className="flex flex-wrap gap-2">
        {vehicles.map((v) => {
          const def = getVehicleDef(v.defId)
          if (!def) return null
          const isSelected = selectedVehicleInstanceIds.includes(v.id)
          const eff = getVehicleEffectiveStatsForInstance(v)
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => onToggle(v.id)}
              className={`flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs transition-colors ${
                isSelected
                  ? 'border-emerald-700 bg-emerald-950/30 text-emerald-200'
                  : 'border-stone-800 bg-stone-800 text-stone-400 hover:border-stone-700'
              }`}
            >
              <span>{def.glyph}</span>
              <span className="font-semibold">{pick(def.name)}</span>
              <span className="text-[10px] opacity-70">
                {t('vehicle_loot_pct', vehicleLootYieldParams(eff.lootMultiplier))} ·{' '}
                {t('assign_vehicle_passenger_seats', { count: eff.passengerSeats })}
              </span>
              <span
                className={`ml-1 text-[10px] ${
                  v.durability > 60
                    ? 'text-emerald-400'
                    : v.durability > 25
                      ? 'text-amber-400'
                      : 'text-red-400'
                }`}
              >
                {v.durability}%
              </span>
            </button>
          )
        })}
        {selectedVehicleInstanceIds.length > 0 && (
          <button
            type="button"
            onClick={() => selectedVehicleInstanceIds.forEach((id) => onToggle(id))}
            className="rounded-md border border-stone-800 bg-stone-800 px-2 py-2 text-[10px] text-stone-500 hover:border-stone-700"
          >
            {t('assign_vehicle_none')}
          </button>
        )}
      </div>
    </div>
  )
}
